import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Bell, Receipt } from "react-bootstrap-icons";
import { useAuthCheck } from "../../utilities/authCheck";
import { initAdminMessaging, onForegroundAdminMessage } from "../../lib/config/firebaseMessaging";
import {
  registerAdminFcmToken,
  fetchAdminNotifications,
  markAdminNotificationRead,
} from "../../lib/webService/webService";

// ─── Module-level CSS ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("nb-styles")) {
  const s = document.createElement("style");
  s.id = "nb-styles";
  s.textContent = `
    @keyframes _nb_pop { 0%{opacity:0;transform:translateY(-6px) scale(.97)} 100%{opacity:1;transform:translateY(0) scale(1)} }
    ._nb_panel { animation: _nb_pop 0.16s ease-out; }
    ._nb_item { transition: background-color 0.15s ease; }
    ._nb_item:hover { background-color: rgba(118,118,128,0.08); }
  `;
  document.head.appendChild(s);
}

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

const timeAgo = (isoDate) => {
  if (!isoDate) return "";
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NotificationBell = () => {
  const { user } = useAuthCheck();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionRequested, setPermissionRequested] = useState(false);

  const refetch = useCallback(async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      const idToken = await user.getIdToken();
      const { notifications: list, unreadCount: count } = await fetchAdminNotifications(idToken);
      setNotifications(list || []);
      setUnreadCount(count || 0);
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Firestore-backed history loads regardless of push permission — the bell
  // is useful even for admins who never grant/enable notifications.
  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const unsubscribe = onForegroundAdminMessage(() => refetch());
    return unsubscribe;
  }, [refetch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = async () => {
    setIsOpen((prev) => !prev);

    // Requesting Notification permission needs a user gesture in some
    // browsers, so this is deferred to the bell's first click rather than
    // fired automatically on mount.
    if (!permissionRequested && typeof Notification !== "undefined" && Notification.permission === "default") {
      setPermissionRequested(true);
      try {
        const fcmToken = await initAdminMessaging();
        if (fcmToken && user) {
          const idToken = await user.getIdToken();
          await registerAdminFcmToken(fcmToken, idToken);
        }
      } catch (error) {
        console.error("Failed to enable push notifications:", error);
      }
    }
  };

  const handleNotificationClick = async (notification) => {
    setIsOpen(false);

    if (!notification.read) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      try {
        const idToken = await user.getIdToken();
        await markAdminNotificationRead(notification.id, idToken);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
        toast.error("Couldn't update notification status");
      }
    }

    navigate(`/sup/orders?orderId=${notification.orderId}`);
  };

  return (
    <div ref={containerRef} style={{ position: "relative", fontFamily: FONT }}>
      <button
        onClick={handleToggle}
        aria-label="Notifications"
        style={{
          position: "relative", width: 38, height: 38, borderRadius: "50%",
          background: "rgba(118,118,128,.10)", border: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", color: "#1c1c1e",
        }}
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute", top: 4, right: 5, minWidth: 16, height: 16,
              borderRadius: 8, background: "#FF3B30", color: "#fff",
              fontSize: "0.625rem", fontWeight: 700, lineHeight: "16px",
              textAlign: "center", padding: "0 3px",
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="_nb_panel"
          style={{
            position: "absolute", right: 0, top: 46, width: 320, maxHeight: 420,
            overflowY: "auto", background: "#FFFFFF", borderRadius: 16,
            border: "1px solid rgba(60,60,67,0.08)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.14)", zIndex: 1000,
          }}
        >
          <div style={{ padding: "14px 16px", borderBottom: "1px solid rgba(60,60,67,0.06)" }}>
            <p style={{ margin: 0, fontSize: "0.875rem", fontWeight: 700, color: "#1c1c1e" }}>
              Notifications
            </p>
          </div>

          {isLoading && notifications.length === 0 ? (
            <div style={{ padding: "28px 16px", textAlign: "center" }}>
              <p style={{ margin: 0, fontSize: "0.8125rem", color: "rgba(60,60,67,0.45)" }}>Loading…</p>
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: "28px 16px", textAlign: "center" }}>
              <p style={{ margin: 0, fontSize: "0.8125rem", color: "rgba(60,60,67,0.45)" }}>
                No notifications yet
              </p>
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                className="_nb_item"
                onClick={() => handleNotificationClick(n)}
                style={{
                  display: "flex", gap: 10, padding: "12px 16px", cursor: "pointer",
                  borderBottom: "1px solid rgba(60,60,67,0.05)",
                  backgroundColor: n.read ? "transparent" : "rgba(0,122,255,0.04)",
                }}
              >
                <div
                  style={{
                    width: 30, height: 30, borderRadius: 8, background: "rgba(0,122,255,0.10)",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2,
                  }}
                >
                  <Receipt size={13} color="#007AFF" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: "0 0 2px", fontSize: "0.8125rem", fontWeight: n.read ? 500 : 700, color: "#1c1c1e" }}>
                    {n.title}
                  </p>
                  <p style={{ margin: "0 0 4px", fontSize: "0.78rem", color: "rgba(60,60,67,0.6)" }}>
                    {n.body}
                  </p>
                  <span style={{ fontSize: "0.7rem", color: "rgba(60,60,67,0.4)" }}>
                    {timeAgo(n.createdAt)}
                  </span>
                </div>
                {!n.read && (
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#007AFF", flexShrink: 0, marginTop: 6 }} />
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
